import type { JsonLd, SEOProps } from '~/hooks/useSEO';
import seoData from '../../seo-pages.json';

interface SeoPageConfig {
  path: string;
  title: string;
  description: string;
  keywords: string;
  changefreq: 'daily' | 'weekly' | 'monthly';
  priority: number;
  schemaType: string;
  schemaExtra?: Record<string, unknown>;
  breadcrumb?: { name: string; path: string }[];
}

const SEO_PAGES = seoData.pages as SeoPageConfig[];

export const SITE_NAME = seoData.siteName;
export const SITE_ORIGIN = seoData.siteOrigin;
export const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/logo256.png`;

export const DEFAULT_ROBOTS = seoData.defaultRobots;
const PRIVATE_ROBOTS = 'noindex, follow';

function pageConfig(path: string): SeoPageConfig {
  const page = SEO_PAGES.find((item) => item.path === path);
  if (!page) throw new Error(`seo-pages.json 缺少 ${path} 的配置`);
  return page;
}

export function pageUrl(path: string) {
  return `${SITE_ORIGIN}${path.startsWith('/') ? path : `/${path}`}`;
}

function clipText(text: string, max: number) {
  const normalized = text.replace(/\s+/g, ' ').trim();
  if (normalized.length <= max) return normalized;
  return `${normalized.slice(0, max - 1)}…`;
}

function breadcrumbJsonLd(page: SeoPageConfig) {
  if (!page.breadcrumb?.length) return undefined;

  const url = pageUrl(page.path);
  return {
    '@type': 'BreadcrumbList',
    '@id': `${url}#breadcrumb`,
    itemListElement: page.breadcrumb.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: pageUrl(item.path),
    })),
  };
}

/** 与 scripts/generate-route-pages.js 使用同一份 seo-pages.json，保证静态 HTML 与运行时一致。 */
function buildPageJsonLd(page: SeoPageConfig): JsonLd {
  const url = pageUrl(page.path);
  const breadcrumb = breadcrumbJsonLd(page);
  const pageNode: Record<string, unknown> = {
    '@type': page.schemaType,
    '@id': `${url}#webpage`,
    url,
    name: page.title,
    description: page.description,
    inLanguage: 'zh-CN',
    isPartOf: {
      '@type': 'WebSite',
      '@id': `${SITE_ORIGIN}/#website`,
      name: SITE_NAME,
      url: `${SITE_ORIGIN}/`,
    },
    ...(page.schemaExtra || {}),
  };

  if (breadcrumb) {
    pageNode.breadcrumb = { '@id': breadcrumb['@id'] };
  }

  return {
    '@context': 'https://schema.org',
    '@graph': breadcrumb ? [breadcrumb, pageNode] : [pageNode],
  };
}

function pageSEO(
  path: string,
  title: string,
  description: string,
  keywords: string,
  robots = DEFAULT_ROBOTS
): SEOProps {
  return {
    title,
    description,
    keywords,
    robots,
    ogUrl: pageUrl(path),
    canonical: pageUrl(path),
  };
}

/** 公开页面的标题、描述来自 seo-pages.json，index.html 与脚本读取同一口径。 */
function publicPageSEO(path: string): SEOProps {
  const page = pageConfig(path);
  return {
    ...pageSEO(path, page.title, page.description, page.keywords),
    // 首页的 WebPage 结构化数据直接写在 index.html 中，运行时不再重复注入
    jsonLd: path === '/' ? undefined : buildPageJsonLd(page),
  };
}

/** 首页文案需与 index.html 保持一致，未执行 JS 的爬虫读的是 HTML。 */
export const HOME_TITLE = pageConfig('/').title;
export const HOME_DESCRIPTION = pageConfig('/').description;
export const HOME_KEYWORDS = pageConfig('/').keywords;

export const HOME_SEO: SEOProps = publicPageSEO('/');

export const WORKFLOW_SEO: SEOProps = publicPageSEO('/workflow');

export const MIGRATION_SEO: SEOProps = publicPageSEO('/workflow/migration');

export const DOWNLOAD_SEO: SEOProps = publicPageSEO('/download');

export const RECHARGE_SEO: SEOProps = publicPageSEO('/recharge');

export const LOGIN_SEO: SEOProps = pageSEO(
  '/login',
  '登录 - 简创AIGC',
  '登录后可使用视频创作、工作流模板和剪映小助手。',
  '登录, 简创AIGC',
  PRIVATE_ROBOTS
);

export const USER_INFO_SEO: SEOProps = pageSEO(
  '/user-info',
  '个人中心 - 简创AIGC',
  '查看和管理你的简创AIGC账户资料。',
  '个人中心, 简创AIGC',
  PRIVATE_ROBOTS
);

export const UPLOAD_SEO: SEOProps = pageSEO(
  '/upload',
  '上传文件 - 简创AIGC',
  '上传视频、图片等素材，供后续创作使用。',
  '上传文件, 简创AIGC',
  PRIVATE_ROBOTS
);

export const BALANCE_SEO: SEOProps = pageSEO(
  '/balance-log',
  '消费明细 - 简创AIGC',
  '查看充值余额与消费明细。',
  '消费明细, 充值余额, 简创AIGC',
  PRIVATE_ROBOTS
);

export const CREATOR_SEO: SEOProps = pageSEO(
  '/workflow?menu=creator',
  '创作者中心 - 简创AIGC',
  '管理你已发布的智能体。',
  '创作者中心, 简创AIGC',
  PRIVATE_ROBOTS
);

export const PURCHASE_SEO: SEOProps = pageSEO(
  '/workflow?menu=record',
  '购买记录 - 简创AIGC',
  '查看你已购买的工作流。',
  '购买记录, 简创AIGC',
  PRIVATE_ROBOTS
);

export const CUSTOM_SEO: SEOProps = pageSEO(
  '/workflow?menu=custom',
  '定制服务 - 简创AIGC',
  '查看你提交的定制需求。',
  '定制服务, 简创AIGC',
  PRIVATE_ROBOTS
);

export const NOT_FOUND_SEO: SEOProps = pageSEO(
  '/404',
  '页面未找到 - 简创AIGC',
  '这个页面不存在，可以返回首页继续浏览。',
  '页面未找到, 简创AIGC',
  PRIVATE_ROBOTS
);

export const ERROR_SEO: SEOProps = pageSEO(
  '/error',
  '访问出错 - 简创AIGC',
  '页面暂时无法打开，请稍后重试或返回首页。',
  '访问出错, 简创AIGC',
  PRIVATE_ROBOTS
);

export const EXTERNAL_FEATURES_SEO: SEOProps = pageSEO(
  '/external-features',
  '剪映小助手 - 简创AIGC',
  '剪映小助手内的说明页，用于草稿下载与版本提示。',
  '剪映小助手, 简创AIGC',
  PRIVATE_ROBOTS
);

export function buildBotSEO({
  name,
  description,
  id,
  image,
  price,
  creatorName,
}: {
  name?: string;
  description?: string;
  id: string;
  image?: string;
  price?: number;
  creatorName?: string;
}): SEOProps {
  const rawName = (name || '').replace(/\s+/g, ' ').trim();
  const titleName = rawName || '智能体详情';
  const shortName = titleName.length > 18 ? `${titleName.slice(0, 17)}…` : titleName;
  const summary = clipText(description || '', 90);
  const path = `/workflow/bot/${encodeURIComponent(id)}`;
  const url = pageUrl(path);
  const absoluteImage = image && /^https?:\/\//.test(image) ? image : undefined;

  const product = rawName
    ? {
        '@type': 'Product',
        '@id': `${url}#product`,
        name: titleName,
        description: summary || '查看该智能体的介绍、包含的工作流和使用说明。',
        category: 'AI 工作流模板',
        ...(absoluteImage ? { image: [absoluteImage] } : {}),
        ...(creatorName ? { brand: { '@type': 'Brand', name: creatorName } } : {}),
        ...(typeof price === 'number' && Number.isFinite(price)
          ? {
              offers: {
                '@type': 'Offer',
                price: String(price),
                priceCurrency: 'CNY',
                availability: 'https://schema.org/InStock',
                url,
              },
            }
          : {}),
      }
    : undefined;

  const breadcrumb = {
    '@type': 'BreadcrumbList',
    '@id': `${url}#breadcrumb`,
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: pageUrl('/') },
      { '@type': 'ListItem', position: 2, name: '工作流商店', item: pageUrl('/workflow') },
      { '@type': 'ListItem', position: 3, name: titleName, item: url },
    ],
  };

  const itemPage: Record<string, unknown> = {
    '@type': 'ItemPage',
    '@id': `${url}#webpage`,
    url,
    name: `${shortName} - ${SITE_NAME}`,
    description: summary || '查看该智能体的介绍、包含的工作流和使用说明。',
    inLanguage: 'zh-CN',
    isPartOf: {
      '@type': 'WebSite',
      '@id': `${SITE_ORIGIN}/#website`,
      name: SITE_NAME,
      url: `${SITE_ORIGIN}/`,
    },
    breadcrumb: { '@id': `${url}#breadcrumb` },
    ...(product ? { mainEntity: { '@id': `${url}#product` } } : {}),
  };

  const graph: Record<string, unknown>[] = [breadcrumb, itemPage];
  if (product) graph.push(product);

  return {
    title: `${shortName} - 简创AIGC`,
    description: summary || '查看该智能体的介绍、包含的工作流和使用说明。',
    keywords:
      rawName && rawName.length <= 20
        ? `${rawName}, 工作流模板, 简创AIGC`
        : '工作流模板, 智能体, 简创AIGC',
    robots: DEFAULT_ROBOTS,
    ogUrl: url,
    canonical: url,
    ogImage: absoluteImage,
    jsonLd: {
      '@context': 'https://schema.org',
      '@graph': graph,
    },
  };
}
